import { createAuthClient } from "better-auth/react";
import { organizationClient } from "better-auth/client/plugins";
import { twoFactorClient } from "better-auth/client/plugins";
import { oauthProviderClient } from "@better-auth/oauth-provider/client";
import { waitForEnvVar } from "./env";

// Singleton auth client that is initialized after runtime env vars are available
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let authClientInstance: any | null = null;

/**
 * Initialize the Better Auth client after ensuring runtime env vars are available.
 * This prevents race conditions where the auth client is created before window.__ENV__ is ready.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function initializeAuthClient(): Promise<any> {
  if (authClientInstance) return authClientInstance;

  const [apiUrl, betterAuthUrl] = await Promise.all([
    waitForEnvVar("VITE_API_URL", "http://localhost:3001"),
    waitForEnvVar("VITE_BETTER_AUTH_URL", ""),
  ]);

  const baseURL = betterAuthUrl || apiUrl || "http://localhost:3001";

  authClientInstance = createAuthClient({
    baseURL,
    plugins: [
      organizationClient(),
      twoFactorClient(),
      // OAuth 2.1 provider support: forwards the signed `oauth_query` from the
      // page URL on sign-in/consent calls so the server can resume the
      // authorization flow (used by MCP clients).
      oauthProviderClient(),
    ],
    // Better-auth handles session management automatically with httpOnly cookies
    sessionOptions: {
      // Don't refetch the session on every visibilitychange. This mirrors our
      // TanStack Query policy (refetchOnWindowFocus: false) and was a driver of
      // OTO-158: each tab-return refetch pumped re-renders through AuthProvider.
      // Session expiry is still handled by the root beforeLoad guard + 401 handling.
      refetchOnWindowFocus: false,
    },
  });

  return authClientInstance;
}

/**
 * Retrieve the initialized auth client.
 * Throws if accessed before initializeAuthClient() has completed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getAuthClient(): any {
  if (!authClientInstance) {
    throw new Error("Auth client not initialized. Call initializeAuthClient() first.");
  }
  return authClientInstance;
}

// Proxy object so existing imports of authClient continue to work after initialization
// Accessors throw if initialization has not been performed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const authClientProxy: any = new Proxy(
  {},
  {
    get(_target, prop: string) {
      const client = getAuthClient();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (client as any)[prop];
    },
  },
);

// Export commonly used authentication methods and hooks (through the proxy)
export const authClient = authClientProxy;

// Wrapper helpers that defer access until after initialization
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const signIn = (...args: any[]) => getAuthClient().signIn(...args);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const signUp = (...args: any[]) => getAuthClient().signUp(...args);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const signOut = (...args: any[]) => getAuthClient().signOut(...args);

export const organization = new Proxy(
  {},
  {
    get(_target, prop: string) {
      return getAuthClient().organization?.[prop as keyof typeof organization];
    },
  },
);

export const twoFactor = new Proxy(
  {},
  {
    get(_target, prop: string) {
      return getAuthClient().twoFactor?.[prop as keyof typeof twoFactor];
    },
  },
);

// Export the client instance for advanced usage
export default authClientProxy;
